import { Key } from './Key'
import { Language, WordLength } from '../types/game'
import { LanguageService } from '../services/languageService'
import { useEffect } from 'react'
import { trackKeyboardUsage } from '../services/gameAnalytics'

// const KEYS = [
//   ['W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
//   ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', "'"],
//   ['ENTER', 'Z', 'C', 'V', 'B', 'N', 'M', 'DELETE'],
// ]

interface KeyboardProps {
  onKey: (key: string) => void
  keyStates: Record<string, 'correct' | 'wrong' | 'wrong-location'>
  language: Language
  wordLength: WordLength
}

export const Keyboard = ({
  onKey,
  keyStates,
  language,
  wordLength,
}: KeyboardProps) => {
  const languageService = LanguageService.getInstance()
  const KEYS = languageService.getKeyboardLayout(language)

  // Handle physical keyboard input
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const key = e.key.toUpperCase()

      // Handle special case for apostrophe (which doesn't get uppercased)
      if (e.key === "'" && language.specialCharacters?.includes("'")) {
        // Prevent browser's quick find feature from activating
        e.preventDefault()

        try {
          // Track keyboard usage
          trackKeyboardUsage("'", wordLength)
        } catch (error) {
          console.error('Failed to track keyboard usage:', error)
        }

        onKey("'")
        return
      }

      // Check if the key is valid for the current language
      if (
        key.length === 1 &&
        /^[A-Z]$/.test(key) &&
        !languageService.isValidCharacter(key, language)
      ) {
        // If invalid for this language, don't process it
        e.preventDefault()
        // Return without handling the key
        return
      }

      // Process valid keys
      if (key === 'ENTER') {
        try {
          trackKeyboardUsage('ENTER', wordLength)
        } catch (error) {
          console.error('Failed to track keyboard usage:', error)
        }
        onKey('ENTER')
      } else if (key === 'BACKSPACE' || key === 'DELETE') {
        try {
          trackKeyboardUsage('DELETE', wordLength, false, true)
        } catch (error) {
          console.error('Failed to track keyboard usage:', error)
        }
        onKey('DELETE')
      } else if (/^[A-Z]$/.test(key)) {
        try {
          // Track keyboard usage with correctness info if available
          const isCorrect = keyStates[key] === 'correct'
          trackKeyboardUsage(key, wordLength, isCorrect)
        } catch (error) {
          console.error('Failed to track keyboard usage:', error)
        }
        onKey(key)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onKey, language, languageService, keyStates, wordLength])

  // Create a wrapper for onKey to track on-screen keyboard usage
  const handleKeyClick = (key: string) => {
    try {
      // Track keyboard usage with correctness info if available
      if (key === 'DELETE') {
        trackKeyboardUsage(key, wordLength, false, true)
      } else if (key === 'ENTER') {
        trackKeyboardUsage(key, wordLength)
      } else {
        const isCorrect = keyStates[key] === 'correct'
        trackKeyboardUsage(key, wordLength, isCorrect)
      }
    } catch (error) {
      console.error('Failed to track keyboard usage:', error)
    }

    onKey(key)
  }

  return (
    <div
      className="keyboard"
      data-testid="keyboard"
      aria-label={`${language.name} keyboard`}
      dir={language.direction}
    >
      {KEYS.map((row, i) => (
        <div key={i} className="keysRow" role="row">
          {row.map((key) => (
            <Key
              key={key}
              value={key}
              onClick={handleKeyClick}
              isEnter={key === 'ENTER'}
              isDelete={key === 'DELETE'}
              state={keyStates[key]}
              data-testid={`key-${key}`}
            />
          ))}
        </div>
      ))}
    </div>
  )
}
